import {
  ArgumentError,
  InvalidInputError,
  PayloadTooLargeError,
  UnknownApiError,
} from './errors';
import { assertRequiredNonEmptyObject, isObject } from './utils';

export default function (api) {
  /**
   * @typedef {Object} TelemetryEvent
   * @property {string} action
   * @property {string} source
   * @property {number} timestamp
   */

  /**
   * @typedef {Object} Feedback
   * @property {string} id
   * @property {Object} data
   * @property {string} data.reason_id
   * @property {string} data.reason_text
   * @property {string} data.freetext
   */

  /**
   * @memberof API
   * @instance
   */
  const tracker = {
    /**
     * Send Telemetry data
     * Does not requires auth token.
     *
     * @method
     * @this API
     *
     * @param {Array.<TelemetryEvent>|TelemetryEvent} payload

     *
     * @return {Promise} promise
     * @fulfill {undefined}
     * @reject {ArgumentError}
     * @reject {InvalidInputError}
     * @reject {PayloadTooLargeError}
     * @reject {UnknownApiError}
     */
    sendTelemetryEvents: async function sendTelemetryEvents(payload = {}) {
      await validateTelemetryPayload(payload); // eslint-disable-line no-use-before-define
      const res = await this.request('post', '/tracker/v1/telemetry-events', {
        data: Array.isArray(payload) ? payload : [payload],
      });

      switch (res.status) {
        case 202: {
          return undefined;
        }
        case 400: {
          const fields = res.body.errors.reduce((acc, error) => ({
            ...acc,
            [error.field]: error.reason,
          }), {});
          throw new InvalidInputError(res.body.message, fields);
        }
        case 413: {
          throw new PayloadTooLargeError();
        }
        default: {
          throw new UnknownApiError('server returned with unhandled status code', res);
        }
      }
    }.bind(api),
    /**
     * Send feedback data
     * Does not requires auth token.
     *
     * @method
     * @this API
     *
     * @param {Feedback} payload

     *
     * @return {Promise} promise
     * @fulfill {undefined}
     * @reject {ArgumentError}
     * @reject {InvalidInputError}
     * @reject {PayloadTooLargeError}
     * @reject {UnknownApiError}
     */
    sendFeedback: async function sendFeedback(payload) {
      assertRequiredNonEmptyObject(payload, 'payload');

      const res = await this.request('post', '/tracker/v1/feedback', { data: payload });

      switch (res.status) {
        case 202: {
          return undefined;
        }
        case 400: {
          const fields = res.body.errors.reduce((acc, error) => ({
            ...acc,
            [error.field]: error.reason,
          }), {});
          throw new InvalidInputError(res.body.message, fields);
        }
        case 413: {
          throw new PayloadTooLargeError();
        }
        default: {
          throw new UnknownApiError('server returned with unhandled status code', res);
        }
      }
    }.bind(api),
  };

  return tracker;
}

const validateTelemetryPayload = (payload) => {
  const badPayloadMsg = 'payload must be an object or an array of objects';

  if (Array.isArray(payload)) {
    if (payload.length === 0) {
      return Promise.reject(new ArgumentError(`${badPayloadMsg} but was empty array`));
    }

    if (payload.find(obj => !isObject(obj)) !== undefined) {
      return Promise.reject(new ArgumentError(badPayloadMsg));
    }
  } else if (isObject(payload)) {
    if (Object.keys(payload) <= 0) {
      return Promise.reject(new ArgumentError(`${badPayloadMsg} but was empty object`));
    }
  } else {
    return Promise.reject(new ArgumentError(badPayloadMsg));
  }

  return Promise.resolve({});
};
